import { ref, computed } from 'vue'
import { defineStore } from 'pinia'
import api from '@/lib/api'
import type { User, AuthState } from '@/types'

export interface AuthUser {
  id: string
  email: string
  name: string
  username: string
  role?: string
}

export const useAuthStore = defineStore('auth', () => {
  // State
  const user = ref<AuthUser | null>(null)
  const token = ref<string | null>(localStorage.getItem('kardapio-token'))
  const refreshToken = ref<string | null>(localStorage.getItem('kardapio-refresh-token'))
  const isLoading = ref(false)
  const error = ref<string | null>(null)

  // Getters
  const isAuthenticated = computed(() => !!token.value)
  const isSuperAdmin = computed(() => {
    // Verificar no localStorage primeiro
    const superAdminFlag = localStorage.getItem('user-is-super-admin')
    if (superAdminFlag === 'true') return true
    
    // Verificar no user
    return user.value?.role === 'super-admin'
  })
  const userRole = computed(() => {
    if (isSuperAdmin.value) return 'SUPER_ADMIN'
    return 'ADMIN'
  })
  const isAdmin = computed(() => !isSuperAdmin.value)
  const isManager = computed(() => !isSuperAdmin.value)
  const isAttendant = computed(() => !isSuperAdmin.value)

  // Actions
  
  /**
   * Login com tokens recebidos do novo endpoint /auth/login
   */
  async function loginWithTokens(
    accessToken: string,
    newRefreshToken: string,
    userData: AuthUser
  ) {
    console.log('[AuthStore] Salvando tokens...')
    
    token.value = accessToken
    refreshToken.value = newRefreshToken
    user.value = userData

    // Salvar no localStorage
    localStorage.setItem('kardapio-token', accessToken)
    localStorage.setItem('kardapio-refresh-token', newRefreshToken)
    
    // Configurar header padrão
    api.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`

    console.log('[AuthStore] Tokens salvos com sucesso')
    return userData
  }

  /**
   * Método antigo de login (mantido para compatibilidade)
   */
  async function login(keycloakToken: string) {
    console.log('[AuthStore] Método antigo de login não suportado mais')
    throw new Error('Use loginWithTokens')
  }

  async function fetchProfile() {
    if (!token.value) return

    try {
      api.defaults.headers.common['Authorization'] = `Bearer ${token.value}`
    } catch (err) {
      logout()
    }
  }

  function logout() {
    console.log('[AuthStore] Logout')
    user.value = null
    token.value = null
    refreshToken.value = null
    error.value = null
    localStorage.removeItem('kardapio-token')
    localStorage.removeItem('kardapio-refresh-token')
    delete api.defaults.headers.common['Authorization']
  }

  function initAuth() {
    const storedToken = localStorage.getItem('kardapio-token')
    const storedRefreshToken = localStorage.getItem('kardapio-refresh-token')
    
    console.log('[AuthStore] initAuth, token existe:', !!storedToken)
    
    if (storedToken) {
      token.value = storedToken
      refreshToken.value = storedRefreshToken
      api.defaults.headers.common['Authorization'] = `Bearer ${storedToken}`
      fetchProfile()
    }
  }

  return {
    user,
    token,
    refreshToken,
    isLoading,
    error,
    isAuthenticated,
    isSuperAdmin,
    userRole,
    isAdmin,
    isManager,
    isAttendant,
    login,
    loginWithTokens,
    logout,
    fetchProfile,
    initAuth,
  }
})
